// Alta de paciente nuevo con consentimiento firmado (Rediseño mayor
// 2026-09-02, Fase 6 — "Nueva consulta" ofrece buscar un paciente
// existente O dar de alta uno nuevo). No hay atajo posible: TODO alta en
// este sistema exige firma (ver crearPaciente en lib/patients.ts,
// packages/web) — mismo texto legal LOPDP que ya usa el panel CRM
// (GET /api/hce/consentimiento-texto, reusa getConsentContent sin
// tocarlo), misma firma en canvas que ya usa el consentimiento de forma
// 024 (SignaturePad.tsx). Al crear con éxito, el paciente queda
// sincronizado a Medplum de inmediato (a diferencia del alta del panel
// CRM, que espera a que se confirme una cita) — el caller entra derecho
// a "Iniciar nueva consulta".
import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { esAdolescenteQueConsiente, esMenorDeEdad, hoyISO } from '@/lib/menorDeEdad';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SignaturePad, type SignaturePadHandle } from '@/formulario/SignaturePad';
import { crearPacienteConsulta, obtenerTextoConsentimiento, type ConsentContent, type ConsentBlock } from '@/lib/hceVault';

function BloqueTexto({ bloque }: { bloque: ConsentBlock }): JSX.Element {
  switch (bloque.kind) {
    case 'subheading':
      return <h5 className="mt-2 text-xs font-semibold uppercase tracking-wide text-hce-ink">{bloque.text}</h5>;
    case 'note':
      return <p className="text-xs italic text-hce-muted">{bloque.text}</p>;
    case 'legalBasis':
      return <p className="text-[11px] text-hce-muted">{bloque.text}</p>;
    case 'list':
      return (
        <ul className="list-disc space-y-0.5 pl-4 text-xs text-hce-ink">
          {bloque.items.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      );
    case 'table':
      return (
        <dl className="text-xs text-hce-ink">
          {bloque.rows.map((r, i) => (
            <div key={i} className="flex justify-between gap-2 border-b border-border py-1">
              <dt className="text-hce-muted">{r.label}</dt>
              <dd>{r.value}</dd>
            </div>
          ))}
        </dl>
      );
    case 'definitions':
      return (
        <dl className="space-y-1 text-xs text-hce-ink">
          {bloque.items.map((d, i) => (
            <div key={i}>
              <dt className="font-medium">{d.term}</dt>
              <dd className="text-hce-muted">{d.text}</dd>
            </div>
          ))}
        </dl>
      );
    case 'paragraph':
    default:
      return <p className="text-xs text-hce-ink">{bloque.text}</p>;
  }
}

export interface NuevoPacienteFormProps {
  readonly onCreado: (r: { patientId: number; medplumPatientId: string }) => void;
  readonly onCancelar: () => void;
}

export function NuevoPacienteForm({ onCreado, onCancelar }: NuevoPacienteFormProps): JSX.Element {
  const { t } = useTranslation();
  const sigRef = useRef<SignaturePadHandle>(null);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [idType, setIdType] = useState<'cedula' | 'pasaporte'>('cedula');
  const [idNumber, setIdNumber] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [repNombre, setRepNombre] = useState('');
  const [repParentesco, setRepParentesco] = useState('');
  const [repCedula, setRepCedula] = useState('');
  const esMenor = esMenorDeEdad(birthDate);
  const adolescente = esAdolescenteQueConsiente(birthDate);

  const [textoLegal, setTextoLegal] = useState<ConsentContent | null>(null);
  const [mostrarTexto, setMostrarTexto] = useState(false);
  const [aceptado, setAceptado] = useState(false);
  const [optDifusion, setOptDifusion] = useState(false);
  const [optWhatsappMeta, setOptWhatsappMeta] = useState(false);
  const [sigVacia, setSigVacia] = useState(true);

  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    obtenerTextoConsentimiento()
      .then(setTextoLegal)
      .catch(() => setError(t('nuevoPaciente.errorTexto')));
  }, [t]);

  const camposObligatoriosCompletos = firstName.trim() && lastName.trim() && idNumber.trim() && phone.trim() && birthDate;

  async function confirmar(): Promise<void> {
    setError(null);
    if (!camposObligatoriosCompletos) {
      setError(t('nuevoPaciente.faltanCampos'));
      return;
    }
    // Validación mínima de formato — el servidor normaliza y es la fuente de
    // verdad, esto solo evita un viaje redondo con "abc" o puro símbolos.
    if (phone.replace(/[^0-9]/g, '').length < 7) {
      setError(t('nuevoPaciente.telefonoInvalido'));
      return;
    }
    if (esMenor && (repNombre.trim().length < 3 || repParentesco.trim().length < 3 || repCedula.trim().length < 5)) {
      setError(t('nuevoPaciente.repObligatorio'));
      return;
    }
    if (!aceptado) {
      setError(t('nuevoPaciente.faltaAceptar'));
      return;
    }
    for (const auth of textoLegal?.optionalAuthorizations ?? []) {
      if (!auth.required) continue;
      const marcado = auth.key === 'difusion' ? optDifusion : optWhatsappMeta;
      if (!marcado) {
        setError(t('nuevoPaciente.faltaAutorizacion', { titulo: auth.title }));
        return;
      }
    }
    if (!sigRef.current || sigRef.current.isEmpty()) {
      setError(t('consentimiento.firmaVacia'));
      return;
    }

    setEnviando(true);
    const resultado = await crearPacienteConsulta({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      idType,
      idNumber: idNumber.trim(),
      phone: phone.trim(),
      email: email.trim() || undefined,
      birthDate,
      consent: {
        signatureDataUrl: sigRef.current.toDataUrl(),
        optDifusion,
        optWhatsappMeta,
        aceptado,
        representative: esMenor ? { fullName: repNombre.trim(), idNumber: repCedula.trim(), relation: repParentesco.trim() } : null,
      },
    });
    setEnviando(false);
    if (!resultado.ok) {
      setError(resultado.detail);
      return;
    }
    onCreado(resultado);
  }

  return (
    <div className="space-y-4">
      {error && (
        <Alert variant="red">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="mb-1 block text-xs font-semibold text-hce-ink">
            {t('nuevoPaciente.nombre')} <span className="text-destructive">*</span>
          </label>
          <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold text-hce-ink">
            {t('nuevoPaciente.apellido')} <span className="text-destructive">*</span>
          </label>
          <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
        </div>
      </div>

      <div className="grid grid-cols-[110px_1fr] gap-3">
        <div>
          <label className="mb-1 block text-xs font-semibold text-hce-ink">{t('nuevoPaciente.tipo')}</label>
          <Select value={idType} onValueChange={(v) => setIdType(v as 'cedula' | 'pasaporte')}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="cedula">{t('nuevoPaciente.cedula')}</SelectItem>
              <SelectItem value="pasaporte">{t('nuevoPaciente.pasaporte')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold text-hce-ink">
            {idType === 'pasaporte' ? t('nuevoPaciente.pasaporte') : t('nuevoPaciente.cedula')} <span className="text-destructive">*</span>
          </label>
          <Input value={idNumber} onChange={(e) => setIdNumber(e.target.value)} />
        </div>
      </div>

      <div>
        <label className="mb-1 block text-xs font-semibold text-hce-ink">
          {t('nuevoPaciente.telefono')} <span className="text-destructive">*</span>
        </label>
        <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+593 99 xxx xxxx" />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="mb-1 block text-xs font-semibold text-hce-ink">{t('nuevoPaciente.email')}</label>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold text-hce-ink">{t('nuevoPaciente.fechaNacimiento')}</label>
          <Input type="date" required max={hoyISO()} value={birthDate} onChange={(e) => setBirthDate(e.target.value)} />
        </div>
      </div>

      {esMenor && (
        <div className="grid grid-cols-3 gap-3">
          <Input value={repNombre} onChange={(e) => setRepNombre(e.target.value)} placeholder={t('nuevoPaciente.repNombre')} aria-label={t('nuevoPaciente.repNombre')} />
          <Input value={repParentesco} onChange={(e) => setRepParentesco(e.target.value)} placeholder={t('nuevoPaciente.repParentesco')} aria-label={t('nuevoPaciente.repParentesco')} />
          <Input value={repCedula} onChange={(e) => setRepCedula(e.target.value)} placeholder={t('nuevoPaciente.repCedula')} aria-label={t('nuevoPaciente.repCedula')} inputMode="numeric" />
        </div>
      )}

      <div className="space-y-3 rounded-lg border border-border bg-hce-paper p-3">
        <div className="flex items-center justify-between gap-2">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-hce-ink">{t('nuevoPaciente.consentimientoLopdp')}</h4>
          <button type="button" onClick={() => setMostrarTexto((v) => !v)} className="shrink-0 text-xs text-hce-pine hover:underline">
            {mostrarTexto || adolescente ? t('nuevoPaciente.ocultarTexto') : t('nuevoPaciente.verTextoCompleto')}
          </button>
        </div>

        {(mostrarTexto || adolescente) && (
          <div className="max-h-64 space-y-3 overflow-y-auto rounded-md border border-border bg-white p-3">
            {!textoLegal ? (
              <p className="text-xs text-hce-muted">{t('dashboard.cargando')}</p>
            ) : (
              textoLegal.sections.map((s) => (
                <div key={s.number} className="space-y-1">
                  <h5 className="text-xs font-semibold text-hce-ink">
                    {s.number}. {s.title}
                  </h5>
                  {s.blocks.map((b, i) => (
                    <BloqueTexto key={i} bloque={b} />
                  ))}
                </div>
              ))
            )}
          </div>
        )}

        <SignaturePad ref={sigRef} onChange={setSigVacia} />

        <label className="flex items-start gap-2 text-xs text-hce-ink">
          <Checkbox checked={aceptado} onCheckedChange={(v) => setAceptado(v === true)} className="mt-0.5" />
          <span>{textoLegal?.acceptLabel ?? t('nuevoPaciente.aceptaDefault')}</span>
        </label>

        {(textoLegal?.optionalAuthorizations ?? []).map((auth) => (
          <label key={auth.key} className="flex items-start gap-2 text-xs text-hce-muted">
            <Checkbox
              checked={auth.key === 'difusion' ? optDifusion : optWhatsappMeta}
              onCheckedChange={(v) => (auth.key === 'difusion' ? setOptDifusion(v === true) : setOptWhatsappMeta(v === true))}
              className="mt-0.5"
            />
            <span>
              {auth.title}
              {auth.required ? ' *' : ''}
            </span>
          </label>
        ))}
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onCancelar}>
          {t('nuevoPaciente.cancelar')}
        </Button>
        <Button disabled={!camposObligatoriosCompletos || sigVacia || enviando} onClick={() => void confirmar()}>
          {enviando ? t('nuevoPaciente.creando') : t('nuevoPaciente.crearYEntrar')}
        </Button>
      </div>
    </div>
  );
}
